'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getJobByIdAction,
  deleteJobAction,
  updateJobAction,
  setActiveJobAction,
  clearActiveJobAction,
  getUserJobsAction,
  getAvailableJobInputFieldsAction,
  createJobAction,
  upsertJobInputAction,
  CreateJob,
  getTeamTagsAction,
  setJobTagsAction,
} from '@/dal/jobs/actions';
import { Job } from '@/lib/db/schema';

const QUERY_KEY = 'jobs';

export function useJobs() {
  const queryClient = useQueryClient();
  const {
    data: jobs,
    error,
    isLoading,
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: getUserJobsAction,
  });

  const createJob = async (data: CreateJob) => {
    const newJob = await createJobAction(data);
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    return newJob;
  };

  const setActiveJob = async (jobId: number) => {
    await setActiveJobAction(jobId);
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
  };

  const clearActiveJob = async () => {
    await clearActiveJobAction();
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
  };

  return {
    jobs,
    isLoading,
    error,
    setActiveJob,
    clearActiveJob,
    createJob,
  };
}

export function useJob(jobId: number) {
  const queryClient = useQueryClient();
  const {
    data: job,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => getJobByIdAction(jobId),
  });

  const updateJob = async (data: Partial<Job>) => {
    await updateJobAction(jobId, data);
    queryClient.invalidateQueries({ queryKey: ['job', jobId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
  };

  const upsertInput = async (inputFieldId: string, value: string) => {
    await upsertJobInputAction(jobId, inputFieldId, value);
    queryClient.invalidateQueries({ queryKey: ['job', jobId] });
  };

  const deleteJob = async () => {
    await deleteJobAction(jobId);
    queryClient.setQueryData(['job', jobId], null);
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
  };

  const setTags = async (tags: string[]) => {
    await setJobTagsAction(jobId, tags);
    queryClient.invalidateQueries({ queryKey: ['job', jobId] });
  };

  return {
    job,
    isLoading,
    error,
    deleteJob,
    updateJob,
    upsertInput,
    setTags,
  };
}

export function useAvailableJobInputFields() {
  const { data, error, isLoading } = useQuery({
    queryKey: ['available-input-fields'],
    queryFn: getAvailableJobInputFieldsAction,
  });

  return {
    availableFields: data || [],
    isLoading,
    isError: error,
  };
}

export function useTeamTags() {
  const { data, error, isLoading } = useQuery({
    queryKey: ['team-tags'],
    queryFn: getTeamTagsAction,
  });

  return {
    tags: data || [],
    isLoading,
    isError: error,
  };
}
